/**
 * Unirse a un consultorio por enlace o QR (pedido del dueño, 3-sep-2026).
 *
 * A diferencia de `/invitacion` (`TokenPages.tsx`, un perfil que YA existe
 * como fila y solo le falta contraseña), acá quien abre el enlace puede no
 * tener cuenta todavía — o puede tenerla y ya estar logueado, en este mismo
 * navegador o en otro. La pantalla cubre las dos ramas; cuál mostrar lo
 * decide `useSession`, igual que en el resto de la aplicación.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordRule } from '@cleanblue/shared';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import { Field, Loading, Note, PasswordInput } from '@/components/ui';
import { AuthShell } from './LoginPage';

const UNIT_ROLE_LABEL: Record<string, string> = { MEMBER: 'Médico', UNIT_ADMIN: 'Administrador' };

interface InviteInfo {
  unit_name: string | null;
  unit_code: string;
  organization_name: string;
  role: 'MEMBER' | 'UNIT_ADMIN';
}

interface JoinResult { ok: true; joined_as_new_account: boolean }

export function JoinUnitPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { user, loading: sessionLoading } = useSession();

  // `retry: false`: un token vencido o ya usado responde 404 siempre —
  // reintentar solo demora el mensaje.
  const invite = useApi<InviteInfo>(token ? `/invite-unit/${token}` : null, { retry: false });

  if (!token) {
    return (
      <AuthShell title="Invitación al consultorio">
        <Note tone="danger">Ese enlace no es válido. Pedile uno nuevo a quien te invitó.</Note>
      </AuthShell>
    );
  }

  if (sessionLoading || invite.isLoading) {
    return (
      <AuthShell title="Invitación al consultorio">
        <Loading label="Revisando el enlace…" />
      </AuthShell>
    );
  }

  if (invite.error || !invite.data) {
    return (
      <AuthShell title="Invitación al consultorio">
        <Note tone="danger">
          {invite.error?.status === 404
            ? 'Ese enlace no es válido, ya se usó, o venció. Pedile uno nuevo a quien te invitó.'
            : (invite.error?.message ?? 'Algo salió mal')}
        </Note>
      </AuthShell>
    );
  }

  const data = invite.data;
  const lugar = data.unit_name ? `${data.unit_name} (${data.unit_code})` : `el consultorio ${data.unit_code}`;
  const rol = UNIT_ROLE_LABEL[data.role] ?? data.role;

  return (
    <AuthShell
      title={`Te invitaron a ${lugar}`}
      subtitle={`${data.organization_name} · te unís como ${rol}`}
    >
      {user
        ? <JoinAsLoggedIn token={token} />
        : <JoinAsGuest token={token} />}
    </AuthShell>
  );
}

/** Igual que `AcceptInvitePage.onDone` (`TokenPages.tsx`): recarga completa,
 *  así el `SessionProvider` arranca de cero con la membresía nueva ya
 *  puesta, en vez de confiar en que la caché de React Query se entere sola. */
function irAlPortal(): void {
  window.location.href = '/';
}

/** Quien ya tenía sesión —otro doctor, o el mismo en otro dispositivo—: un
 *  solo botón, sin formulario. El backend vuelve a comprobar el token por su
 *  cuenta, así que no hace falta repetir acá la validación del `GET`. */
function JoinAsLoggedIn({ token }: { token: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function unirse() {
    setBusy(true);
    setError(null);
    try {
      await api.post<JoinResult>(`/invite-unit/${token}/join`);
      irAlPortal();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Algo salió mal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Note tone="danger">{error}</Note>}
      <p className="small muted mt-2 mb-2">Vas a entrar con tu cuenta ya abierta.</p>
      <button
        type="button"
        className="btn btn--primary btn--block btn--lg"
        disabled={busy}
        onClick={() => void unirse()}
      >
        {busy ? 'Uniéndome…' : 'Unirme'}
      </button>
    </>
  );
}

/** Quien no tiene cuenta todavía: se registra con correo y contraseña —la
 *  misma regla de contraseña que el resto de la aplicación (`passwordRule`)—
 *  y el mismo POST crea la cuenta, la membresía y la sesión de una vez. */
function JoinAsGuest({ token }: { token: string }) {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const rule = passwordRule.safeParse(password);
  const tooShort = password.length > 0 && !rule.success;
  const mismatch = confirm.length > 0 && confirm !== password;

  /* Si ya tiene cuenta con ese correo (`email_taken`), el enlace se pierde al
     ir a `/ingresar` a secas —por eso el `state.from` acá, y no el simple
     `navigate('/ingresar')` de `AcceptInvitePage`: ahí perder el enlace no
     importa (esa invitación era solo para poner contraseña a un perfil que
     ya existía), acá sin volver a esta URL la persona nunca se une al
     consultorio. */
  function irAIngresar() {
    navigate('/ingresar', { state: { from: `/invitacion-consultorio?token=${token}` } });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!rule.success || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post<JoinResult>(`/invite-unit/${token}/join`, {
        full_name: fullName, email, password,
      });
      irAlPortal();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('unknown', 'Algo salió mal', 500));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-2">
      {error && (
        <Note tone="danger">
          {error.message}
          {error.code === 'email_taken' && (
            <>
              {' '}
              <button type="button" className="btn btn--ghost btn--sm" onClick={irAIngresar}>Ingresá</button>
            </>
          )}
        </Note>
      )}

      <Field label="Tu nombre" hint="Como querés que aparezca en el sistema">
        <input
          className="input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoFocus
          required
        />
      </Field>

      <Field label="Correo" hint="Es tu usuario para entrar al portal.">
        <input
          className="input"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Field>

      <Field
        label="Contraseña"
        hint="Mínimo 8 caracteres."
        error={tooShort ? rule.error?.issues[0]?.message : undefined}
      >
        <PasswordInput
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          required
        />
      </Field>

      <Field label="Repetila" error={mismatch ? 'Las dos contraseñas no coinciden' : undefined}>
        <PasswordInput
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          required
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block btn--lg"
        disabled={busy || !rule.success || mismatch}
      >
        {busy ? 'Creando cuenta…' : 'Crear cuenta y unirme'}
      </button>

      <p className="muted small mt-3" style={{ textAlign: 'center' }}>
        ¿Ya tenés cuenta?{' '}
        <button type="button" className="btn btn--ghost btn--sm" onClick={irAIngresar}>Ingresá</button>
      </p>
    </form>
  );
}
